 (function () {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const storage = {
    getItem(key) { try { return window.localStorage.getItem(key); } catch (_) { return null; } },
    setItem(key, value) { try { window.localStorage.setItem(key, value); } catch (_) { /* Checkout still works without browser storage. */ } }
  };
  let cartReturnFocus = null;
  let cartChangePending = false;
  let cartRequestQueue = Promise.resolve();
  const promoOfferDurationMs = 45 * 60 * 1000;
  function formatThemeMoney(cents) {
    const currency = typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : 'CAD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency }).format(cents / 100); } catch (error) { return '$' + (cents / 100).toFixed(2); }
  }

  function ensurePromoOfferExpiry(code) {
    if (!code || !storage) return 0;
    const now = Date.now();
    const current = Number(storage.getItem('schroderPromoOfferEndsAt') || 0);
    if (current > now) return current;
    const expiresAt = now + promoOfferDurationMs;
    storage.setItem('schroderPromoOfferEndsAt', String(expiresAt));
    return expiresAt;
  }

  function updatePromoReservation() {
    const code = storage ? storage.getItem('schroderPromoCode') : '';
    const storedExpiry = Number(storage.getItem('schroderPromoOfferEndsAt') || 0);
    const expiresAt = code ? (storedExpiry || ensurePromoOfferExpiry(code)) : 0;
    const remaining = Math.max(0, expiresAt - Date.now());
    qsa('[data-promo-reservation]').forEach((reservation) => {
      const active = Boolean(code && expiresAt > Date.now());
      reservation.hidden = !active;
      if (!active) return;
      const timer = qs('[data-promo-timer]', reservation);
      const minutes = String(Math.floor(remaining / 60000)).padStart(2, '0');
      const seconds = String(Math.floor(remaining / 1000) % 60).padStart(2, '0');
      if (timer) timer.textContent = `${minutes}:${seconds}`;
      reservation.setAttribute('aria-label', `Reserving offer for ${minutes} minutes ${seconds} seconds`);
    });
  }

  function updateCartOfferSummary() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    const subtotalNode = qs('[data-cart-subtotal-cents]', drawer);
    if (!subtotalNode) return;
    const code = storage ? storage.getItem('schroderPromoCode') : '';
    const discountRow = qs('[data-cart-discount-row]', drawer);
    const discountCode = qs('[data-cart-discount-code]', drawer);
    const discountAmount = qs('[data-cart-discount-amount]', drawer);
    if (discountRow) discountRow.hidden = !code;
    if (discountCode) discountCode.textContent = code || '';
    if (discountAmount) discountAmount.textContent = 'Eligibility checked at checkout';
  }

  function setCartCount(count) {
    qsa('[data-cart-count]').forEach((element) => { element.textContent = count; });
  }

  let cartPendingRequests = 0;
  let cartNeedsRefresh = false;
  let cartSyncPromise = null;

  function setCartPending(pending) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    if (pending) drawer.setAttribute('aria-busy', 'true');
    else drawer.removeAttribute('aria-busy');
    let status = qs('[data-cart-status]', drawer);
    if (!status && pending) {
      status = document.createElement('p');
      status.setAttribute('data-cart-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.className = 'cart-drawer__discount-status';
      (qs('.cart-drawer__body', drawer) || drawer).prepend(status);
    }
    if (status) {
      status.hidden = !pending;
      status.textContent = pending ? window.theme.strings.updatingCart || 'Updating your cart…' : '';
    }
    qsa('[data-cart-change]', drawer).forEach(control => {
      if (pending || cartNeedsRefresh) {
        if (!control.hasAttribute('data-cart-was-disabled')) control.dataset.cartWasDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.hasAttribute('data-cart-was-disabled')) {
        control.disabled = control.dataset.cartWasDisabled === 'true';
        delete control.dataset.cartWasDisabled;
      }
    });
    qsa('[data-cart-checkout]', drawer).forEach(link => {
      if (pending || cartNeedsRefresh) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    });
    qsa('[data-cart-retry]', drawer).forEach(button => { button.disabled = pending; });
  }

  function showCartSyncError(message) {
    const drawer = qs('[data-cart-drawer]');
    const error = drawer && qs('[data-cart-error]', drawer);
    if (!error) return;
    error.hidden = false;
    error.textContent = message;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'cart-item__remove';
    retry.setAttribute('data-cart-retry', '');
    retry.textContent = window.theme.strings.retryCart || 'Retry cart update';
    error.appendChild(retry);
  }

  // Keep responses in order when different product forms are submitted quickly.
  function queueCartRequest(request) {
    cartPendingRequests += 1;
    setCartPending(true);
    const pending = cartRequestQueue.then(request).finally(() => {
      cartPendingRequests -= 1;
      setCartPending(cartPendingRequests > 0);
    });
    cartRequestQueue = pending.catch(() => {});
    return pending;
  }

  function requestCartSync() {
    if (cartSyncPromise) return cartSyncPromise;
    cartNeedsRefresh = true;
    cartSyncPromise = queueCartRequest(async () => {
      try {
        await refreshCart();
      } catch (_) {
        showCartSyncError(window.theme.strings.cartSyncError || 'Your cart could not be refreshed. Please retry before changing quantities.');
      }
    }).finally(() => { cartSyncPromise = null; });
    return cartSyncPromise;
  }

  const cartHistoryKey = 'schroderCartDrawer';
  let cartHistoryClosing = false;
  let cartHistoryReopen = false;

  function isCartHistoryEntry() {
    return window.history?.state?.[cartHistoryKey] === true;
  }

  function openCart(fromHistory = false) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    if (cartHistoryClosing && !fromHistory) {
      cartHistoryReopen = true;
      return;
    }
    if (!fromHistory && !isCartHistoryEntry()) {
      try {
        window.history.pushState({ ...window.history.state, [cartHistoryKey]: true }, '', window.location.href);
      } catch (_) {}
    }
    if (drawer.hidden && !drawer.contains(document.activeElement)) cartReturnFocus = document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      if (drawer.getAttribute('aria-hidden') === 'false') drawer.classList.add('is-open');
    });
    document.body.classList.add('no-scroll');
    qs('button[data-cart-close]', drawer)?.focus();
    if (window.CustomEvent) document.dispatchEvent(new window.CustomEvent('schroder:cart-visibility', { detail: { open: true } }));
  }

  function closeCart(fromHistory = false) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer || drawer.hidden || drawer.getAttribute('aria-hidden') === 'true') return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    if (window.CustomEvent) document.dispatchEvent(new window.CustomEvent('schroder:cart-visibility', { detail: { open: false } }));
    cartReturnFocus?.focus();
    window.setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 420);
    if (!fromHistory && isCartHistoryEntry() && !cartHistoryClosing) {
      cartHistoryClosing = true;
      try {
        window.history.back();
      } catch (_) {
        cartHistoryClosing = false;
      }
    }
  }

  function syncCartHistory() {
    cartHistoryClosing = false;
    if (cartHistoryReopen) {
      cartHistoryReopen = false;
      openCart();
    } else if (isCartHistoryEntry()) {
      openCart(true);
    } else {
      closeCart(true);
    }
  }

  window.addEventListener('popstate', syncCartHistory);
  window.addEventListener('pageshow', event => {
    syncCartHistory();
    requestCartSync();
  });
  window.addEventListener('online', () => { if (cartNeedsRefresh) requestCartSync(); });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && qs('[data-cart-drawer].is-open')) requestCartSync();
  });

  function renderCart(cart, sections, openAfterRefresh = false) {
    const currentDrawer = qs('[data-cart-drawer]');
    const sectionHtml = sections && sections['cart-drawer'];
    if (!sectionHtml) throw new Error(window.theme.strings.cartError);
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const updatedDrawer = qs('[data-cart-drawer]', parsed);
    if (!currentDrawer || !updatedDrawer) throw new Error(window.theme.strings.cartError);
    // /cart/add.js returns added lines, not the full cart count. Use Liquid's count.
    const itemCount = Number(updatedDrawer.getAttribute('data-cart-item-count') ?? cart?.item_count);
    if (!Number.isInteger(itemCount) || itemCount < 0) throw new Error(window.theme.strings.cartError);
    const wasOpen = currentDrawer.getAttribute('aria-hidden') === 'false';
    const scrollTop = qs('.cart-drawer__body', currentDrawer)?.scrollTop || 0;
    const focused = currentDrawer.contains(document.activeElement) ? document.activeElement : null;
    const focusedId = focused?.id;
    const couponValue = qs('[data-cart-discount-input]', currentDrawer)?.value;
    const panel = qs('.cart-drawer__panel', currentDrawer);
    const updatedPanel = qs('.cart-drawer__panel', updatedDrawer);
    if (panel && updatedPanel) panel.replaceChildren(...updatedPanel.childNodes);
    else currentDrawer.replaceChildren(...updatedDrawer.childNodes);
    currentDrawer.setAttribute('data-cart-item-count', String(itemCount));
    cartNeedsRefresh = false;
    setCartCount(itemCount);
    setupShippingProgress();
    updateCartOfferSummary();
    updatePromoReservation();
    const body = qs('.cart-drawer__body', currentDrawer);
    if (body) body.scrollTop = scrollTop;
    const coupon = qs('[data-cart-discount-input]', currentDrawer);
    if (coupon && couponValue !== undefined) coupon.value = couponValue;
    setCartPending(cartPendingRequests > 0);
    if (openAfterRefresh && !wasOpen) openCart();
    else if (wasOpen && focused) {
      const focusTarget = focusedId && document.getElementById(focusedId);
      (focusTarget || qs('.cart-item__title, button[data-cart-close]', currentDrawer))?.focus({ preventScroll: true });
    }
  }

  async function renderCartResponse(data, openAfterRefresh = false) {
    try {
      renderCart(data, data.sections, openAfterRefresh);
    } catch (_) {
      // A section can fail after the add succeeds. Refresh without adding twice.
      try {
        await refreshCart(openAfterRefresh);
      } catch (_) {
        cartNeedsRefresh = true;
        const message = window.theme.strings.cartUpdatedSyncError || 'Your cart changed, but its display could not be refreshed. Retry the cart update; do not add the item again.';
        showCartSyncError(message);
        throw new Error(message);
      }
    }
  }

  async function refreshCart(openAfterRefresh = false) {
    try {
      const sectionResponse = await fetch(window.theme.routes.cartSections, { cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!sectionResponse.ok) throw new Error(window.theme.strings.cartError);
      const sections = await sectionResponse.json();
      renderCart(null, sections, openAfterRefresh);
      return sections;
    } catch (error) {
      cartNeedsRefresh = true;
      throw error;
    }
  }

  async function changeCartItem(button) {
    if (button.disabled || cartChangePending || cartNeedsRefresh || cartPendingRequests > 0) return;
    const drawer = button.closest('[data-cart-drawer]');
    const quantity = Number(button.dataset.quantity);
    const key = button.dataset.lineKey;
    const line = Number(button.dataset.line);
    if (!Number.isInteger(quantity) || quantity < 0 || (!key && (!Number.isInteger(line) || line < 1))) return;
    const status = drawer && qs('[data-cart-error]', drawer);
    if (status) { status.hidden = true; status.textContent = ''; }
    cartChangePending = true;
    const originalLabel = button.textContent;
    if (quantity === 0) button.textContent = window.theme.strings.removingCartItem || 'Removing…';
    try {
      await queueCartRequest(async () => {
        const response = await fetch(window.theme.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...(key ? { id: key } : { line }), quantity, sections: ['cart-drawer'], sections_url: window.theme.routes.cart })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || window.theme.strings.cartError);
        await renderCartResponse(cart);
      });
    } catch (error) {
      if (cartNeedsRefresh) {
        showCartSyncError(error.message || window.theme.strings.cartError);
      } else if (status) {
        status.textContent = error.message || window.theme.strings.cartError;
        status.hidden = false;
      } else window.alert(error.message || window.theme.strings.cartError);
    } finally {
      cartChangePending = false;
      if (button.isConnected) button.textContent = originalLabel;
    }
  }

  async function addToCart(form, buyNow, submitter) {
    if (form.dataset.forceSoldOut === 'true') return;
    if (form.dataset.submitting === 'true') return;
    if (!form.reportValidity()) return;
    const buttons = qsa('[data-add-to-cart], [data-buy-now]', form);
    if (submitter?.disabled) return;
    const previous = buttons.map((button) => button.disabled);
    let errorBox = qs('[data-product-error]', form);
    if (!errorBox) {
      errorBox = document.createElement('p');
      errorBox.setAttribute('data-product-error', '');
      errorBox.setAttribute('role', 'alert');
      form.appendChild(errorBox);
    }
    if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
    const payload = new FormData(form);
    const checkout = buyNow || submitter?.dataset.buyNow !== undefined;
    if (!checkout) {
      payload.set('sections', 'cart-drawer');
      payload.set('sections_url', window.theme.routes.cart);
    }
    const activeButton = submitter || qs('[data-add-to-cart]', form);
    const label = activeButton && (qs('[data-add-label]', activeButton) || Array.from(activeButton.childNodes).find(node => node.nodeType === 3 && node.textContent.trim()));
    const previousLabel = label?.textContent;
    form.dataset.submitting = 'true';
    if (label) label.textContent = window.theme.strings.addingToCart || 'Adding…';
    buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    if (!checkout) openCart();
    try {
      await queueCartRequest(async () => {
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: payload
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || window.theme.strings.cartError);
        if (typeof window.CustomEvent === 'function') {
          try { document.dispatchEvent(new window.CustomEvent('schroder:cart-added', { detail: data })); } catch (_) {}
        }
        const promoCode = storage ? storage.getItem('schroderPromoCode') : '';
        if (checkout) {
          const destination = window.theme.routes.checkout || '/checkout';
          window.location.href = promoCode ? `${window.theme.routes.root || '/'}discount/${encodeURIComponent(promoCode)}?redirect=${encodeURIComponent(destination)}` : destination;
        } else await renderCartResponse(data);
      });
    } catch (error) {
      if (!checkout) showCartSyncError(error.message || window.theme.strings.cartError);
      if (errorBox) {
        errorBox.textContent = error.message || window.theme.strings.cartError;
        errorBox.hidden = false;
        errorBox.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      } else window.alert(window.theme.strings.cartError);
    } finally {
      delete form.dataset.submitting;
      if (label) label.textContent = previousLabel;
      buttons.forEach((button, index) => { button.disabled = previous[index]; button.removeAttribute('aria-busy'); });
      form.dispatchEvent(new Event('product:submit-complete'));
    }
  }

  function setupMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    const toggle = qs('[data-mobile-menu-toggle]');
    const close = qs('[data-mobile-menu-close]');
    if (!menu || !toggle) return;
    const closeMenu = () => { menu.hidden = true; toggle.setAttribute('aria-expanded', 'false'); document.body.classList.remove('no-scroll'); toggle.focus(); };
    toggle.addEventListener('click', () => {
      menu.hidden = !menu.hidden;
      toggle.setAttribute('aria-expanded', String(!menu.hidden));
      document.body.classList.toggle('no-scroll', !menu.hidden);
      if (!menu.hidden) close?.focus();
    });
    if (close) close.addEventListener('click', closeMenu);
    qsa('a', menu).forEach((link) => link.addEventListener('click', closeMenu));
  }

  function setupDesktopMegaMenu() {
    qsa('.nav-item--featured').forEach((item) => {
      if (item.dataset.megaMenuReady) return;
      const trigger = item.querySelector(':scope > [data-nav-toggle]');
      const collectionLink = item.querySelector(':scope > a');
      const menu = item.querySelector(':scope > .mega-menu');
      if (!trigger || !menu) return;

      let closeTimer;
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      const header = item.closest('.site-header');
      const fitMenuToViewport = () => {
        if (!header || !desktopQuery.matches) return;
        const availableHeight = Math.max(0, window.innerHeight - Math.max(0, header.getBoundingClientRect().bottom) - 16);
        menu.style.setProperty('--menu-available-height', `${availableHeight}px`);
      };
      const openMenu = () => {
        document.dispatchEvent(new CustomEvent('header:menu-open', { detail: item }));
        window.clearTimeout(closeTimer);
        fitMenuToViewport();
        item.classList.add('is-open');
        item.classList.remove('is-focus-closed');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const closeMenu = () => {
        window.clearTimeout(closeTimer);
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      };
      const scheduleClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(closeMenu, 160);
      };
      const fitOpenMenu = () => { if (item.classList.contains('is-open')) fitMenuToViewport(); };
      window.addEventListener('resize', fitOpenMenu, { passive: true });
      window.addEventListener('scroll', fitOpenMenu, { passive: true });

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') openMenu();
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') scheduleClose();
      });
      menu.addEventListener('pointerenter', openMenu);
      menu.addEventListener('pointerleave', scheduleClose);
      trigger.addEventListener('blur', () => {
        if (!item.contains(document.activeElement)) scheduleClose();
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) scheduleClose();
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu();
          item.classList.add('is-focus-closed');
          trigger.focus();
        }
        if (event.key === 'ArrowDown' && [trigger, collectionLink].includes(document.activeElement)) {
          event.preventDefault();
          openMenu();
          menu.querySelector('a')?.focus();
        }
      });
      trigger.addEventListener('click', (event) => {
        if (!desktopQuery.matches) return;
        event.preventDefault();
        if (item.classList.contains('is-open')) { closeMenu(); item.classList.add('is-focus-closed'); }
        else openMenu();
      });
      document.addEventListener('header:search-open', closeMenu);
      document.addEventListener('header:menu-open', event => { if (event.detail !== item) closeMenu(); });
      document.addEventListener('pointerdown', (event) => {
        if (!item.contains(event.target)) closeMenu();
      });
      window.addEventListener('resize', () => {
        if (!desktopQuery.matches) closeMenu();
      });
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));

      trigger.setAttribute('aria-expanded', 'false');
      item.dataset.megaMenuReady = 'true';
    });
  }

  function setupPromoOffers() {
    qsa('[data-copy-promo]').forEach((offer) => {
      if (offer.dataset.promoBound) return;
      offer.dataset.promoBound = 'true';
      offer.addEventListener('click', async () => {
        const code = offer.dataset.promoCode;
        if (!code) return;
        if (storage) storage.setItem('schroderPromoCode', code);
        ensurePromoOfferExpiry(code);
        const labels = qsa('[data-promo-label]', offer);
        const codeLabel = qs('[data-promo-code-label]', offer);
        if (offer.dataset.applyPromoCart !== undefined) {
          const drawer = qs('[data-cart-drawer]');
          const input = drawer ? qs('[data-cart-discount-input]', drawer) : null;
          const status = drawer ? qs('[data-cart-discount-status]', drawer) : null;
          if (input) input.value = code;
          if (status) status.textContent = `${code} saved · eligibility and savings are confirmed at checkout.`;
          if (drawer) qs('[data-cart-discount-form]', drawer)?.classList.add('is-applied');
          labels.forEach((label) => { label.textContent = 'SAVED FOR CHECKOUT'; });
          if (codeLabel) codeLabel.textContent = `${code} ✓`;
          offer.classList.add('is-applied');
          offer.setAttribute('aria-label', `Discount code ${code} saved for checkout.`);
          updateCartOfferSummary();
          updatePromoReservation();
          openCart();
          return;
        }
        let promoCopied = false;
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(code);
            promoCopied = true;
          } else {
            const helper = document.createElement('textarea');
            helper.value = code;
            helper.setAttribute('readonly', '');
            helper.style.position = 'fixed';
            helper.style.opacity = '0';
            document.body.appendChild(helper);
            helper.select();
            promoCopied = document.execCommand('copy');
            helper.remove();
          }
        } catch (error) {
          window.prompt('Copy this discount code:', code);
        }
        if (offer.dataset.promoCopiedLabel && !promoCopied) {
          labels.forEach((label) => { label.textContent = offer.dataset.promoManualLabel || 'Copy code'; });
          offer.classList.remove('is-copied');
          offer.setAttribute('aria-label', `Copy discount code ${code} manually. It is saved for checkout.`);
          return;
        }
        labels.forEach((label) => { label.textContent = offer.dataset.promoCopiedLabel || 'CODE COPIED'; });
        if (codeLabel && offer.dataset.promoKeepCode === undefined) codeLabel.textContent = `${code} ✓`;
        offer.classList.add('is-copied');
        offer.setAttribute('aria-label', `Discount code ${code} copied and saved for checkout.`);
      });
    });
  }

  function setupProduct(section = qs('[data-product-section]')) {
    if (!section || section.dataset.productReady) return;
    const dataElement = qs('[data-product-json]', section);
    let product = null;
    try { product = JSON.parse(dataElement.textContent); } catch (error) { return; }
    const form = qs('[data-product-form]', section);
    if (!form) return;
    section.dataset.productReady = 'true';
    const variantInput = qs('[data-variant-id]', form);
    const price = qs('[data-product-price]', section);
    const submitButtons = qsa('[data-add-to-cart], [data-buy-now]', form);
    const options = qsa('.product-option input[type="radio"]', form);

    function formatMoney(cents) {
      const currency = typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : 'CAD';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency }).format(cents / 100); } catch (error) { return '$' + (cents / 100).toFixed(2); }
    }

    function selectedVariant() {
      if (!options.length) return product.variants.find((variant) => String(variant.id) === variantInput.value) || product.variants[0] || null;
      const selected = [];
      qsa('.product-option', form).forEach((option) => {
        const input = qs('input:checked', option);
        if (input) selected.push(input.value);
      });
      return product.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;
    }

    function updateVariant() {
      const variant = selectedVariant();
      const available = Boolean(variant && variant.available && form.dataset.forceSoldOut !== 'true');
      if (variant) {
        variantInput.value = variant.id;
        if (price) price.innerHTML = formatMoney(variant.price) + (variant.compare_at_price > variant.price ? ' <s>' + formatMoney(variant.compare_at_price) + '</s>' : '');
      } else variantInput.value = '';
      submitButtons.forEach((button) => {
        button.disabled = !available || form.dataset.submitting === 'true';
        const label = qs('[data-add-label]', button) || button;
        const text = button.dataset.buyNow !== undefined ? window.theme.strings.buyNow || 'Buy now' : window.theme.strings.addToCart;
        label.textContent = available ? text : variant ? window.theme.strings.soldOut : window.theme.strings.unavailable;
      });
      const availability = qs('.product-info__availability', section);
      if (availability) {
        availability.classList.toggle('is-sold', !available);
        const label = qs('[data-availability-label]', availability);
        if (label) label.textContent = available ? window.theme.strings.readyToShip : variant ? window.theme.strings.soldOut : window.theme.strings.unavailable;
      }
      const delivery = qs('[data-delivery-urgency]', section);
      if (delivery) delivery.hidden = !available;
      const note = qs('[data-product-price-note]', section);
      if (note) note.textContent = available ? window.theme.strings.priceNote : variant ? window.theme.strings.soldOut : window.theme.strings.unavailable;
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url);
      }
    }
    options.forEach((input) => input.addEventListener('change', updateVariant));
    form.addEventListener('product:submit-complete', updateVariant);
    updateVariant();
  }

  function setupQuantityControls() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
      if (!button) return;
      const form = button.closest('form');
      const input = form && qs('input[name="quantity"]', form);
      if (!input) return;
      const delta = button.hasAttribute('data-quantity-plus') ? 1 : -1;
      input.value = Math.max(1, Number(input.value || 1) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function setupCollection() {
    const sort = qs('[data-sort-by]');
    if (sort) {
      const current = new URL(window.location.href).searchParams.get('sort_by');
      if (current) sort.value = current;
      sort.addEventListener('change', () => { const url = new URL(window.location.href); url.searchParams.set('sort_by', sort.value); window.location.href = url.toString(); });
    }
    const panel = qs('[data-filter-panel]');
    const toggle = qs('[data-filter-toggle]');
    const close = qs('[data-filter-close]');
    if (panel && toggle) {
      toggle.addEventListener('click', () => { panel.classList.add('is-open'); toggle.setAttribute('aria-expanded', 'true'); document.body.classList.add('no-scroll'); });
      if (close) close.addEventListener('click', () => { panel.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); document.body.classList.remove('no-scroll'); });
    }
  }

  function setupShippingProgress() {
    const progress = qs('[data-shipping-progress]');
    const message = qs('[data-shipping-message]', progress || document);
    if (!progress) return;
    const bar = qs('span', progress);
    if (bar) bar.style.width = '100%';
    if (message) message.textContent = window.theme.strings.freeShippingIncluded || 'Free shipping included on every order.';
  }

  function setupScrollReveals() {
    const items = qsa('[data-reveal], [data-stagger]').filter((item) => !item.closest('[data-home-scroll]'));
    if (!items.length) return;
    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    items.forEach((item) => observer.observe(item));
  }

  function formatCartMoney(cents) {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100); } catch (error) { return '$' + (cents / 100).toFixed(2); }
  }

  document.addEventListener('click', (event) => {
    const productCard = event.target.closest('[data-product-card]');
    if (productCard && productCard.dataset.productUrl && !event.target.closest('a, button, input, select, textarea, form')) {
      window.location.href = productCard.dataset.productUrl;
      return;
    }
    const open = event.target.closest('[data-cart-open]');
    const close = event.target.closest('[data-cart-close]');
    const change = event.target.closest('[data-cart-change]');
    const checkout = event.target.closest('[data-cart-checkout]');
    if (open) { event.preventDefault(); openCart(); requestCartSync(); }
    if (close) { event.preventDefault(); closeCart(); }
    if (event.target.closest('[data-cart-retry]')) { event.preventDefault(); requestCartSync(); }
    if (checkout) {
      event.preventDefault();
      if (checkout.getAttribute('aria-disabled') === 'true') return;
      const code = storage ? storage.getItem('schroderPromoCode') : '';
      const destination = checkout.getAttribute('href') || window.theme.routes.checkout || '/checkout';
      window.location.href = code ? `${window.theme.routes.root || '/'}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(destination)}` : destination;
    }
    if (change) {
      event.preventDefault();
      changeCartItem(change);
    }
  });

  document.addEventListener('submit', (event) => {
    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (discountForm) {
      event.preventDefault();
      const input = qs('[data-cart-discount-input]', discountForm);
      const status = qs('[data-cart-discount-status]');
      const code = input ? input.value.trim() : '';
      if (!code) {
        if (status) status.textContent = 'Enter a discount code to apply it at checkout.';
        return;
      }
      if (storage) storage.setItem('schroderPromoCode', code);
      ensurePromoOfferExpiry(code);
      if (status) status.textContent = `${code} saved · eligibility and savings are confirmed at checkout.`;
      discountForm.classList.add('is-applied');
      updateCartOfferSummary();
      updatePromoReservation();
      return;
    }
    const form = event.target.closest('.js-product-form');
    if (form) { event.preventDefault(); addToCart(form, event.submitter && event.submitter.dataset.buyNow !== undefined, event.submitter); }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { closeCart(); const menu = qs('[data-mobile-menu]'); if (menu && !menu.hidden) qs('[data-mobile-menu-toggle]').click(); }
    const drawer = qs('[data-cart-drawer].is-open');
    if (event.key !== 'Tab' || !drawer) return;
    const focusable = qsa('a[href], button:not(:disabled), input:not(:disabled), select, textarea', drawer).filter((node) => node.getClientRects().length);
    const first = focusable[0]; const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  function setupRecommendations() {
    qsa('[data-product-recommendations]').forEach((element) => {
      if (element.dataset.recommendationsBound || element.dataset.recommendationsLoaded) return;
      element.dataset.recommendationsBound = 'true';
      const load = async () => {
        try {
          const response = await fetch(element.dataset.recommendationsUrl, { headers: { Accept: 'text/html' } });
          if (!response.ok) return;
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = parsed.querySelector('[data-product-recommendations]');
          if (!element.isConnected || !content?.querySelector('.product-card')) return;
          element.replaceChildren(...content.childNodes);
          element.dataset.recommendationsLoaded = 'true';
          setupPromoOffers();
        } catch (_) { /* Optional recommendations must not interrupt purchasing. */ }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(); }
        }, { rootMargin: '400px' });
        observer.observe(element);
      } else load();
    });
  }
  setupRecommendations();
  setupMobileMenu();
  setupDesktopMegaMenu();
  setupPromoOffers();
  setupProduct();
  if (isCartHistoryEntry()) openCart(true);
  document.addEventListener('shopify:section:load', (event) => {
    qsa('[data-product-section]', event.target).forEach(setupProduct);
    setupPromoOffers();
    setupRecommendations();
  });
  setupQuantityControls();
  setupCollection();
  setupShippingProgress();
  setupScrollReveals();
  updateCartOfferSummary();
  updatePromoReservation();
  window.setInterval(() => { updateCartOfferSummary(); updatePromoReservation(); }, 1000);
}());
(function () {
  'use strict';

  document.querySelectorAll('[data-action-gallery]').forEach((gallery) => {
    if (gallery.dataset.actionReady === 'true') return;

    const track = gallery.querySelector('[data-action-track]');
    const pause = gallery.querySelector('[data-action-pause]');
    const pauseLabel = gallery.querySelector('[data-action-pause-label]');
    if (!track || !pause) return;

    const motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const speed = Math.max(50, Math.min(120, Number(gallery.dataset.actionSpeed) || 78));
    let paused = Boolean(motionQuery && motionQuery.matches);
    let inViewport = true;
    let focused = false;

    track.style.setProperty('--action-marquee-duration', `${speed}s`);

    const syncPlayback = () => {
      track.style.animationPlayState = paused || !inViewport || focused || document.hidden ? 'paused' : 'running';
    };

    const setPaused = (value) => {
      paused = value;
      pause.setAttribute('aria-pressed', String(paused));
      if (pauseLabel) pauseLabel.textContent = paused ? 'Play reel' : 'Pause reel';
      syncPlayback();
    };

    pause.addEventListener('click', () => setPaused(!paused));

    gallery.addEventListener('focusin', () => {
      focused = true;
      syncPlayback();
    });
    gallery.addEventListener('focusout', () => {
      window.setTimeout(() => {
        focused = gallery.contains(document.activeElement);
        syncPlayback();
      }, 0);
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        inViewport = entries[0].isIntersecting;
        syncPlayback();
      }, { threshold: 0.12 });
      observer.observe(gallery);
    }
    document.addEventListener('visibilitychange', syncPlayback);
    if (motionQuery) {
      const onMotionChange = () => {
        paused = motionQuery.matches;
        pause.setAttribute('aria-pressed', String(paused));
        if (pauseLabel) pauseLabel.textContent = paused ? 'Play reel' : 'Pause reel';
        syncPlayback();
      };
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
      else if (motionQuery.addListener) motionQuery.addListener(onMotionChange);
    }

    setPaused(paused);
   gallery.dataset.actionReady = 'true';
  });
}());
